import { RateAggregate } from './rates-cache';

/**
 * Grades one production line (product x machine) against that combo's own
 * history and reports whether it under-performed.
 *
 * Two independent axes, because they fail for different reasons and a run can
 * be bad at one while fine at the other:
 *
 *   - throughput: bultos/hr where the combo records bultos, kg/hr otherwise;
 *                 low is bad
 *   - merma:      waste / (waste + kilos), high is bad
 *
 * Everything here is derived at render time from the batch aggregate. Nothing
 * is stored: "low performer" is a statement about a run *relative to a moving
 * baseline*, so a value frozen at save time would describe a comparison that no
 * longer exists. (`order_productions.performance` was a stored column once and
 * was dropped in 2022 for the same reason.)
 */

// Fraction of the baseline below which throughput counts as under-performing.
export const THROUGHPUT_WARN_RATIO = 0.8;
export const THROUGHPUT_BAD_RATIO = 0.6;

// Merma is inverted — above the baseline is the bad direction — so the
// thresholds are the reciprocals of the throughput ones. That keeps "20% worse
// than usual" meaning the same thing on both axes.
export const MERMA_WARN_RATIO = 1 / THROUGHPUT_WARN_RATIO; // 1.25
export const MERMA_BAD_RATIO = 1 / THROUGHPUT_BAD_RATIO; // ~1.667

// A baseline thinner than this is not evidence. Counted AFTER the graded run is
// removed, so a combo needs 11 recorded runs before its 11th can be judged.
export const MIN_BASELINE_RUNS = 10;

export type FlagSeverity = 'ok' | 'warn' | 'bad';

// Why a line could not be graded, so the UI can say so instead of implying the
// run was fine.
export type FlagUngradable =
    | 'pre-epoch' // run predates hour capture; no comparable baseline exists
    | 'no-hours' // in-era line whose hours were never captured
    | 'thin-history' // fewer than MIN_BASELINE_RUNS other runs
    | 'pending'; // aggregate still in flight

export interface AxisGrade {
    severity: FlagSeverity;
    // The run's own value: a per-hour rate for throughput, 0..1 for merma.
    value: number | null;
    // The self-excluded baseline, same units.
    baseline: number | null;
    // value / baseline for throughput, also value / baseline for merma. Above 1
    // is good for throughput and bad for merma.
    ratio: number | null;
    ungradable: FlagUngradable | null;
    // Runs behind the baseline, excluding this one.
    baselineRuns: number;
}

/**
 * Throughput is measured in whichever unit the combo actually keeps.
 *
 * Bultos are what corte counts and what a shift is planned in, so they are the
 * unit an operator recognises. But plenty of lines — extrusión rollos above all
 * — are only ever weighed, and their `groups` is 0. Grading those in bultos/hr
 * would divide zero by hours and call every run catastrophic, so kilos are the
 * fallback rather than the other way round.
 */
export type ThroughputUnit = 'bultos' | 'kilos';

// One unit's reading. `ratio === null` means this unit cannot be used at all —
// either the run has none of it or the baseline has none of it — which is
// exactly the test for whether bultos are "available".
export interface ThroughputReading {
    value: number | null;
    baseline: number | null;
    ratio: number | null;
}

export interface ThroughputGrade extends AxisGrade {
    // Which reading decided `severity` (and mirrors into value/baseline/ratio).
    unit: ThroughputUnit;
    // Both readings, so a surface that has room — the form's Rendimiento tab —
    // can print kg/hr and bultos/hr side by side. Only `unit`'s reading is
    // graded; the other is informational.
    kilos: ThroughputReading;
    bultos: ThroughputReading;
}

/**
 * Which history the line was measured against.
 *
 * 'machine' means this product×machine combo did not have enough runs of its
 * own and the machine's whole history stood in. That is a weaker comparison —
 * the machine's average mixes every product it makes — so it must be visible
 * wherever a number derived from it is shown, not silently substituted.
 */
export type BaselineSource = 'combo' | 'machine';

export interface LineGrade {
    machineId: number;
    productId: number;
    throughput: ThroughputGrade;
    merma: AxisGrade;
    // The worse of the two severities — what the row tint and the card chip use.
    severity: FlagSeverity;
    baselineSource: BaselineSource;
}

const NO_READING: ThroughputReading = {
    value: null,
    baseline: null,
    ratio: null,
};

// Keep what the run itself did, drop the comparison. Used wherever the rate is
// computable but the baseline is one we are not willing to stand behind — a
// history too thin to be evidence, or a bultos average taken across a machine's
// whole product mix.
const ownRateOnly = (reading: ThroughputReading): ThroughputReading => ({
    value: reading.value,
    baseline: null,
    ratio: null,
});

const UNGRADABLE = (reason: FlagUngradable, baselineRuns = 0): AxisGrade => ({
    severity: 'ok',
    value: null,
    baseline: null,
    ratio: null,
    ungradable: reason,
    baselineRuns,
});

const UNGRADABLE_THROUGHPUT = (
    reason: FlagUngradable,
    baselineRuns = 0,
    readings?: { kilos: ThroughputReading; bultos: ThroughputReading },
): ThroughputGrade => ({
    ...UNGRADABLE(reason, baselineRuns),
    unit: 'kilos',
    kilos: readings?.kilos ?? NO_READING,
    bultos: readings?.bultos ?? NO_READING,
});

const worse = (a: FlagSeverity, b: FlagSeverity): FlagSeverity => {
    if (a === 'bad' || b === 'bad') return 'bad';
    if (a === 'warn' || b === 'warn') return 'warn';
    return 'ok';
};

// Whether a run falls inside the analysis window, whose lower bound the caller
// passes as `windowFrom` (HOURLY_DATA_EPOCH by default). Runs before it are out
// of scope on both counts: below the epoch their hours read 0 and they would
// look infinitely slow, and — with a moved "Desde" — the aggregate they would be
// judged against never counted them. Ungradable, not bad.
const isInWindow = (startDate: string, windowFrom: string): boolean =>
    startDate >= windowFrom;

/**
 * What this run already contributes to the aggregate, so it can be taken back
 * out before the baseline is computed.
 *
 * This is NOT the same as the values being graded, and conflating the two is
 * the trap. On a saved row they coincide. On an edit form they do not: the
 * aggregate holds the SAVED kilos/hours while the user is typing new ones, so
 * subtracting what was typed would leave part of this run inside its own
 * baseline. On a new production there is nothing to subtract at all.
 *
 * It carries its own date because window membership follows the *saved* date —
 * changing the date field in a form does not move what the aggregate already
 * counted.
 */
export interface RecordedContribution {
    startDate: string;
    kilos: number;
    groups: number;
    hours: number | null;
    wasteShare: number;
}

// The baseline the run is judged against, in raw sums so both units and the
// merma ratio can be derived from the same self-excluded window.
interface BaselineWindow {
    kilos: number;
    groups: number;
    hours: number;
    waste: number;
    runs: number;
}

/**
 * Read the single window off the aggregate and subtract the run's recorded
 * contribution from it.
 *
 * One window since 2026-08-11 — the rolling 12-month "recent" window is gone, so
 * there is nothing to choose between; `all_*` is every run the aggregate was
 * fetched with, i.e. since `windowFrom`.
 *
 * The subtraction only holds if the run is actually inside that window. The
 * aggregate is fetched with fromDate = windowFrom, so a run before it (a
 * pre-epoch corrida, or one before a moved "Desde") is absent from the sums, and
 * subtracting it would eat some other run's kilos rather than clean this one's
 * out. gradeLine refuses out-of-window runs outright, so this is belt-and-braces
 * — but the window is passed in, not assumed.
 */
function selfExcludedWindow({
    rate,
    recorded,
    windowFrom,
}: {
    rate: RateAggregate;
    recorded: RecordedContribution | null;
    windowFrom: string;
}): BaselineWindow {
    const window = {
        kilos: rate.all_kilos,
        groups: rate.all_groups,
        hours: rate.all_hours,
        waste: rate.all_waste,
        runs: rate.all_runs,
    };

    // Nothing recorded yet (a production being captured for the first time) —
    // the aggregate cannot contain it, so there is nothing to take back out.
    if (recorded === null) return window;

    if (!isInWindow(recorded.startDate, windowFrom)) return window;

    return {
        kilos: Math.max(0, window.kilos - recorded.kilos),
        groups: Math.max(0, window.groups - recorded.groups),
        hours: Math.max(0, window.hours - (recorded.hours ?? 0)),
        waste: Math.max(0, window.waste - recorded.wasteShare),
        runs: Math.max(0, window.runs - 1),
    };
}

/**
 * One unit's reading against the window.
 *
 * `zeroIsAbsent` is the difference between the two units and the reason this is
 * not one shared formula. Zero kilos in eight hours is a real, terrible result.
 * Zero bultos is almost always "this line is not counted in bultos" — extrusión
 * rollos are weighed, never bagged — so it means *no reading*, not a bad one.
 * The baseline side is absent at zero for both: a history with no bultos gives
 * nothing to compare against whichever way the run was captured.
 */
function readThroughput({
    amount,
    hours,
    windowAmount,
    windowHours,
    zeroIsAbsent,
}: {
    amount: number;
    hours: number;
    windowAmount: number;
    windowHours: number;
    zeroIsAbsent: boolean;
}): ThroughputReading {
    const value =
        hours <= 0 || (zeroIsAbsent && amount <= 0) ? null : amount / hours;
    const baseline =
        windowAmount > 0 && windowHours > 0 ? windowAmount / windowHours : null;
    return {
        value,
        baseline,
        ratio:
            value !== null && baseline !== null && baseline > 0
                ? value / baseline
                : null,
    };
}

/**
 * Grade one line. `rate` is the machine x product aggregate; `machineRate` is
 * the machine-level fallback used when the combo does not carry
 * MIN_BASELINE_RUNS runs of its own, matching the planning board's
 * product-first rule.
 *
 * `kilos` / `groups` / `hours` / `wasteShare` / `startDate` are the values being
 * JUDGED. `recorded` is what the aggregate already contains for this run — see
 * RecordedContribution. Omit it on a saved row (the two coincide); pass `null`
 * for a production that does not exist yet; pass the saved values when grading
 * an edit in progress.
 *
 * `windowFrom` is the lower bound of the window `rate`/`machineRate` were
 * fetched with — HOURLY_DATA_EPOCH for the list and planning board, the tab's
 * "Desde" for the form. It is passed rather than assumed because the aggregate's
 * window is not this function's to guess: self-exclusion subtracts a run only if
 * the window contains it, and a movable window it could not see would corrupt
 * every baseline.
 */
export function gradeLine({
    machineId,
    productId,
    startDate,
    kilos,
    groups,
    hours,
    wasteShare,
    recorded,
    rate,
    machineRate,
    windowFrom,
}: {
    machineId: number;
    productId: number;
    startDate: string;
    kilos: number;
    // Bultos on this line. 0 means the line is not counted in bultos, which
    // sends throughput grading to kilos rather than scoring it as zero output.
    groups: number;
    hours: number | null;
    // This line's prorated share of the production's waste, in kg.
    wasteShare: number;
    recorded?: RecordedContribution | null;
    rate: RateAggregate | undefined;
    machineRate: RateAggregate | undefined;
    // Lower bound of the window the aggregates cover; see the doc block above.
    windowFrom: string;
}): LineGrade {
    const base = { machineId, productId };

    // A run outside the window cannot be graded on EITHER axis. Below the epoch
    // its hours read 0; before a moved "Desde" the only baseline available does
    // not cover it — grading a run against a history that excludes it compares
    // two different periods and would flag runs for having been out of scope.
    if (!isInWindow(startDate, windowFrom)) {
        return {
            ...base,
            throughput: UNGRADABLE_THROUGHPUT('pre-epoch'),
            merma: UNGRADABLE('pre-epoch'),
            severity: 'ok',
            baselineSource: 'combo',
        };
    }

    const ungradable = (
        reason: FlagUngradable,
        baselineSource: BaselineSource = 'combo',
    ): LineGrade => ({
        ...base,
        throughput: UNGRADABLE_THROUGHPUT(reason),
        merma: UNGRADABLE(reason),
        severity: 'ok',
        baselineSource,
    });

    // Omitted means "the values shown are the values recorded" — the saved row
    // case, which is the majority of callers.
    const contribution =
        recorded === undefined
            ? { startDate, kilos, groups, hours, wasteShare }
            : recorded;

    // An aggregate that has not arrived is unknown, not empty. Reporting
    // thin-history before the answer landed would settle the question early and
    // make the chip flicker.
    if (rate === undefined) return ungradable('pending');

    const comboWindow = selfExcludedWindow({
        rate,
        recorded: contribution,
        windowFrom,
    });

    // The machine fallback now covers THIN history, not just absent history: a
    // combo with three runs of its own is no more judgeable than one with none,
    // and the machine's whole record is the only other thing to compare against.
    // It is a weaker baseline — it averages every product the machine makes —
    // so `baselineSource` travels with the grade and every surface says so.
    //
    // Only this LINE's contribution comes back out of the machine window. A
    // production running two products on one machine leaves its other line in,
    // which flatters the baseline very slightly; taking the whole production out
    // would need the sibling lines, which the flags deliberately do not have.
    let window = comboWindow;
    let baselineSource: BaselineSource = 'combo';
    if (comboWindow.runs < MIN_BASELINE_RUNS) {
        if (machineRate === undefined) return ungradable('pending');
        const machineWindow = selfExcludedWindow({
            rate: machineRate,
            recorded: contribution,
            windowFrom,
        });
        // Only if it is actually a wider history. The machine aggregate is a
        // superset of the combo's, so this normally holds; the guard keeps a
        // degenerate cache entry from making the baseline worse.
        if (machineWindow.runs > comboWindow.runs) {
            window = machineWindow;
            baselineSource = 'machine';
        }
    }

    // Still thin after the fallback: refused per axis rather than returned
    // early, so the run count survives into the UI's "Historial insuficiente
    // (N corridas)" note.
    const thin = window.runs < MIN_BASELINE_RUNS;

    // ---- throughput ----
    // Bultos first, kilos as the fallback: bultos are the unit the floor counts
    // in, but plenty of lines are only ever weighed and carry groups = 0.
    const kilosReading = readThroughput({
        amount: kilos,
        hours: hours ?? 0,
        windowAmount: window.kilos,
        windowHours: window.hours,
        zeroIsAbsent: false,
    });
    // Bultos are compared only against the combo's OWN history. A bulto is not
    // a machine cycle — it is a bundle of bags, so `bultos/hr = cycle rate ÷
    // bags-per-bulto`, and that divisor is an arbitrary per-product packaging
    // decision: product A may bundle 100 bags at 5 kg while product B bundles
    // 50 at 25 kg, and B then looks five times slower on identical work.
    // Comparing a product against itself cancels the convention out; averaging
    // it across a machine's whole product mix does not, and would stack
    // packaging variance on top of the product variance kg/hr already carries.
    // So a machine baseline keeps the run's own bultos/hr and drops the bultos
    // comparison, which sends the grading to kilos — a fixed physical unit.
    const rawBultos = readThroughput({
        amount: groups,
        hours: hours ?? 0,
        windowAmount: window.groups,
        windowHours: window.hours,
        zeroIsAbsent: true,
    });
    const bultosReading =
        baselineSource === 'machine' ? ownRateOnly(rawBultos) : rawBultos;

    let throughput: ThroughputGrade;
    if (!hours || hours <= 0) {
        // Without hours neither unit has a denominator, so the readings above
        // are meaningless — report the refusal with no numbers attached.
        throughput = UNGRADABLE_THROUGHPUT('no-hours', window.runs);
    } else if (thin) {
        // The run's own rates are perfectly computable and worth showing on the
        // form — it is the BASELINE that is not evidence. So the readings come
        // through with their baseline and ratio stripped, and the note says why.
        throughput = UNGRADABLE_THROUGHPUT('thin-history', window.runs, {
            kilos: ownRateOnly(kilosReading),
            bultos: ownRateOnly(bultosReading),
        });
    } else {
        const unit: ThroughputUnit =
            bultosReading.ratio !== null ? 'bultos' : 'kilos';
        const graded = unit === 'bultos' ? bultosReading : kilosReading;
        const { ratio } = graded;
        throughput = {
            severity:
                ratio === null
                    ? 'ok'
                    : ratio < THROUGHPUT_BAD_RATIO
                      ? 'bad'
                      : ratio < THROUGHPUT_WARN_RATIO
                        ? 'warn'
                        : 'ok',
            value: graded.value,
            baseline: graded.baseline,
            ratio,
            ungradable: ratio === null ? 'thin-history' : null,
            baselineRuns: window.runs,
            unit,
            kilos: kilosReading,
            bultos: bultosReading,
        };
    }

    // ---- merma ----
    // Merma needs no hours, so a pre-2026 run is still gradable on this axis.
    let merma: AxisGrade;
    const denominator = kilos + wasteShare;
    if (thin) {
        merma = UNGRADABLE('thin-history', window.runs);
    } else if (denominator <= 0) {
        // Nothing produced and nothing wasted — no ratio to speak of.
        merma = UNGRADABLE('no-hours', window.runs);
    } else {
        const value = wasteShare / denominator;
        const baselineDenominator = window.kilos + window.waste;
        const baseline =
            baselineDenominator > 0
                ? window.waste / baselineDenominator
                : null;
        // A zero baseline means the combo has never wasted anything; any waste
        // at all is then a real regression, but a ratio would divide by zero.
        const ratio =
            baseline === null ? null : baseline > 0 ? value / baseline : null;
        merma = {
            severity:
                ratio === null
                    ? baseline === 0 && value > 0
                        ? 'warn'
                        : 'ok'
                    : ratio > MERMA_BAD_RATIO
                      ? 'bad'
                      : ratio > MERMA_WARN_RATIO
                        ? 'warn'
                        : 'ok',
            value,
            baseline,
            ratio,
            ungradable: null,
            baselineRuns: window.runs,
        };
    }

    return {
        ...base,
        throughput,
        merma,
        severity: worse(throughput.severity, merma.severity),
        baselineSource,
    };
}

export interface ProductionGrade {
    // Keyed `${productId}-${machineId}` so a card row can look itself up.
    byLine: Map<string, LineGrade>;
    // Worst severity across the production's lines — drives the header chip.
    severity: FlagSeverity;
    // The worst line on each axis, for the chip's tooltip.
    worstThroughput: LineGrade | null;
    worstMerma: LineGrade | null;
}

export const lineKey = (productId: number, machineId: number): string =>
    `${productId}-${machineId}`;

/** Roll per-line grades up to the card. */
export function summarizeGrades(lines: LineGrade[]): ProductionGrade {
    const byLine = new Map<string, LineGrade>();
    lines.forEach((line) =>
        byLine.set(lineKey(line.productId, line.machineId), line),
    );

    const rank: Record<FlagSeverity, number> = { ok: 0, warn: 1, bad: 2 };
    const pickWorst = (
        axis: (line: LineGrade) => AxisGrade,
    ): LineGrade | null =>
        lines.reduce<LineGrade | null>((worstLine, line) => {
            if (axis(line).severity === 'ok') return worstLine;
            if (!worstLine) return line;
            return rank[axis(line).severity] > rank[axis(worstLine).severity]
                ? line
                : worstLine;
        }, null);

    return {
        byLine,
        severity: lines.reduce<FlagSeverity>(
            (acc, line) => worse(acc, line.severity),
            'ok',
        ),
        worstThroughput: pickWorst((line) => line.throughput),
        worstMerma: pickWorst((line) => line.merma),
    };
}
